/**
 * ConsentPanel — split out of AuthHub.jsx in 2026-02.
 */
import { useState } from "react";

import { toast } from "sonner";
import { ArrowLeft, Check } from "@phosphor-icons/react";
import UnifiedAgreementContent from "../../components/legal/UnifiedAgreementContent";

import {
  GLASS, FieldLabel, TextInput, PasswordInput, DisplayField,
  GoldButton, GhostBackButton, useAuthShellBackground, parseCardDate,
} from "./_shared";

export default function ConsentPanel({ role, onAgreed, onBack }) {
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [agreed, setAgreed] = useState(false);
  useAuthShellBackground();

  // We mark the agreement as "fully read" the moment the scroll position is
  // within 32px of the bottom — small tolerance so users on devices with
  // rubber-band scrolling don't get stuck just because they over-scrolled a
  // pixel short of the absolute end.
  const onScroll = (e) => {
    const el = e.currentTarget;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance < 32 && !scrolledToEnd) setScrolledToEnd(true);
  };

  return (
    <div
      className="font-tajawal flex flex-col"
      dir="rtl"
      data-testid="consent-fullscreen"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background:
          "linear-gradient(180deg,#E4E4E4 0%, #E4E4E4 calc(env(safe-area-inset-top, 0px) + 40px), #E0E0E0 40%, #D0D0D0 75%, #C7C7C7 100%)",
        paddingTop: "calc(env(safe-area-inset-top, 0px) + 10px)",
        paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 10px)",
      }}
    >
      {/* Compact header — fixed, never scrolls */}
      <div className="px-4 sm:px-6 shrink-0">
        <GhostBackButton onClick={onBack} testid="consent-back-btn" />
        <h1 className="text-[19px] font-extrabold text-[#0A192F] leading-tight text-center mb-0.5">
          الشُّروط وَالسِّياسات
        </h1>
        <p className="text-[11.5px] text-[#5A6470] text-center mb-2">
          لِإنشاء حِسابك كَـ{role === "lawyer" ? "مُحامٍ" : "زائر / عَميل"}
        </p>
        <div
          className="p-2.5 rounded-xl bg-[#FFF8E1] border border-[#D4AF37]/50 text-[11.5px] text-[#7A5A00] leading-snug text-center"
          data-testid="consent-notice"
        >
          <b>ملاحظة</b>: يَجِب عَلَيك قِراءَة جَميع الشُّروط وَالسِّياسات حَتّى النِّهاية وَالمُوافَقة عَلَيها لِمُتابَعة التَّسجيل.
        </div>
      </div>

      {/* Scrollable agreement — fills the remaining vertical space */}
      <div
        onScroll={onScroll}
        data-testid="agreement-scroll"
        className="flex-1 overflow-y-auto mx-3 sm:mx-4 mt-2 bg-white/90 border border-white/70 rounded-2xl shadow-inner [scrollbar-width:thin]"
        dir="rtl"
      >
        <div className="px-3 sm:px-4 py-3">
          <UnifiedAgreementContent />

          {/* End-of-document marker. The checkbox below this only becomes
              interactive after the user has actually scrolled to it. */}
          <div className="h-px" data-testid="agreement-bottom-sentinel" />

          {!scrolledToEnd && (
            <p
              className="text-[12px] text-[#A88425] text-center mt-3 mb-1 font-bold animate-pulse"
              data-testid="scroll-hint"
            >
              ⇣ تابِع التَّمرير لِأَسفَل لِإكمال القِراءَة ⇣
            </p>
          )}

          <label
            className={`mt-3 mb-1 flex items-start gap-3 p-3 rounded-2xl transition border ${
              !scrolledToEnd
                ? "bg-[#F3F4F6] border-[#E5E7EB] opacity-60 cursor-not-allowed"
                : agreed
                ? "bg-[#EFFBF1] border-[#16A34A] cursor-pointer"
                : "bg-white border-[#D4AF37] cursor-pointer hover:border-[#A88425]"
            }`}
            data-testid="consent-agreement-row"
            onClick={(e) => {
              if (!scrolledToEnd) e.preventDefault();
            }}
          >
            <span
              className={`mt-0.5 w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition ${
                agreed && scrolledToEnd
                  ? "bg-[#16A34A] text-white"
                  : "bg-white border-2 border-[#5A6470]"
              }`}
            >
              {agreed && scrolledToEnd && <Check size={14} weight="bold" />}
            </span>
            <input
              type="checkbox"
              className="sr-only"
              checked={agreed && scrolledToEnd}
              disabled={!scrolledToEnd}
              onChange={(e) => setAgreed(e.target.checked)}
              data-testid="consent-agreement-checkbox"
            />
            <div className="flex-1 min-w-0 text-right">
              <div className="text-[13.5px] font-extrabold text-[#0A192F] leading-snug">
                أُوافِق على شُروط الاِستِخدام وَسِياسة الخُصوصيّة
              </div>
              <div className="text-[11px] text-[#5A6470] mt-0.5 leading-snug">
                {scrolledToEnd
                  ? "قَرَأتُ الاِتّفاقيّة بِالكامِل وَأُوافِق عَلَيها كَوَحدة قانونيّة واحِدة."
                  : "اِقرَأ الاِتّفاقيّة حَتّى النِّهاية لِتَتَمَكَّن مِن المُوافَقة."}
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Sticky bottom action — always visible */}
      <div className="px-4 sm:px-6 pt-2.5 shrink-0">
        <GoldButton
          onClick={() => onAgreed()}
          disabled={!agreed || !scrolledToEnd}
          testid="consent-continue"
        >
          <span>أُوافِق وَأَتابِع</span>
          <ArrowLeft size={18} weight="bold" />
        </GoldButton>
        <p className="text-[10.5px] text-[#5A6470] text-center mt-1.5 leading-snug">
          بِالنَّقر على «أُوافِق وَأَتابِع» يَتِمّ تَسجيل مُوافَقَتك (التَّاريخ + IP مُجَزَّأ) كَدَليل قانونيّ — App Store · Google Play · GDPR.
        </p>
      </div>
    </div>
  );
}
